import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import CarCard from "../components/CarCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICE_RANGES = [
  { value: "any", min: 0, max: Infinity },
  { value: "lt50", min: 0, max: 50000 },
  { value: "50-100", min: 50000, max: 100000 },
  { value: "100-200", min: 100000, max: 200000 },
  { value: "gt200", min: 200000, max: Infinity },
];

export default function Cars() {
  const { t } = useLang();
  const n = t("nx.cars");
  const [cars, setCars] = useState(null);
  const [make, setMake] = useState("any");
  const [price, setPrice] = useState("any");
  const [status, setStatus] = useState("any");

  useEffect(() => {
    fetch(`${API}/cars`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setCars).catch(() => setCars([]));
  }, []);

  const makes = useMemo(() => (cars ? [...new Set(cars.map((c) => c.make))].sort() : []), [cars]);
  const filtered = useMemo(() => {
    if (!cars) return [];
    const range = PRICE_RANGES.find((r) => r.value === price);
    return cars.filter((c) =>
      (make === "any" || c.make === make) &&
      (status === "any" || c.status === status) &&
      Number(c.price_eur) >= range.min && Number(c.price_eur) < range.max
    );
  }, [cars, make, price, status]);

  return (
    <div className="bg-[#050607] min-h-screen" data-testid="cars-page">
      <section className="px-5 md:px-10 pt-40 pb-14 md:pt-48 md:pb-16 border-b border-white/10">
        <div className="eyebrow mb-8">{n.eyebrow}</div>
        <h1 className="font-display text-[36px] sm:text-[54px] lg:text-[64px] text-[#F3F3F0] max-w-5xl">
          {n.title.split("\n").map((l, i) => <span key={i} className="block">{l}</span>)}
        </h1>
        <p className="mt-6 text-[14px] text-[#999995] max-w-2xl">{n.sub}</p>
      </section>

      <section className="px-5 md:px-10 py-12 md:py-16">
        <div className="flex flex-col md:flex-row gap-4 md:items-center mb-12" data-testid="cars-filters">
          <select className="field-input md:max-w-[200px] cursor-pointer" value={make} onChange={(e) => setMake(e.target.value)} data-testid="cars-filter-make" aria-label={n.filters.make}>
            <option value="any">{n.filters.allMakes}</option>
            {makes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="field-input md:max-w-[200px] cursor-pointer" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="cars-filter-price" aria-label={n.filters.price}>
            <option value="any">{n.filters.anyPrice}</option>
            <option value="lt50">&lt; 50.000 €</option>
            <option value="50-100">50.000 – 100.000 €</option>
            <option value="100-200">100.000 – 200.000 €</option>
            <option value="gt200">&gt; 200.000 €</option>
          </select>
          <select className="field-input md:max-w-[200px] cursor-pointer" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cars-filter-status" aria-label={n.filters.status}>
            <option value="any">{n.filters.allStatuses}</option>
            <option value="available">{n.statusLabels.available}</option>
            <option value="reserved">{n.statusLabels.reserved}</option>
            <option value="sold">{n.statusLabels.sold}</option>
          </select>
          <span className="mono text-[11px] text-[#666663] uppercase tracking-[0.14em]" data-testid="cars-count">
            {cars ? `${filtered.length} / ${cars.length}` : "…"}
          </span>
        </div>

        {cars === null && <p className="mono text-[12px] text-[#666663]">…</p>}
        {cars && cars.length === 0 && (
          <div className="border border-white/10 bg-[#0A0B0C] p-10 md:p-16 text-center" data-testid="cars-empty">
            <p className="text-[14px] text-[#999995] max-w-md mx-auto mb-8">{n.empty}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/contact" className="btn btn-solid justify-between" data-testid="cars-empty-cta">
                <span>{t("nx.homeOpp.cta")}</span><span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        )}
        {cars && cars.length > 0 && filtered.length === 0 && (
          <p className="mono text-[12px] text-[#666663] uppercase tracking-[0.14em]" data-testid="cars-no-match">{n.noMatch}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
        </div>
      </section>
    </div>
  );
}
